import { computed, nextTick, ref, toValue, type MaybeRefOrGetter } from "vue";
import axios from "axios";
import route from "ziggy-js";
import { create } from "@/composables/useNotifications";
import { useTranslations } from "@/composables/useTranslations";

export function useDomainConnectApply(
    domainName: MaybeRefOrGetter<string>,
    desiredDmarcRecord: MaybeRefOrGetter<string>,
    userSourceDestination: MaybeRefOrGetter<string> = "wizard",
) {
    const { t } = useTranslations();
    const submitting = ref(false);

    const desiredRecord = computed(() =>
        (toValue(desiredDmarcRecord) ?? "").toString().trim(),
    );

    const getErrorMessage = (error: unknown): string => {
        const e = error as Record<string, unknown> | undefined;
        const data = e?.response as Record<string, unknown> | undefined;
        const body = data?.data as Record<string, unknown> | undefined;
        const message = body?.message;

        if (typeof message === "string" && message.length > 0) {
            return message;
        }

        const errors = body?.errors;
        if (errors && typeof errors === "object") {
            for (const value of Object.values(errors)) {
                if (Array.isArray(value) && typeof value[0] === "string") {
                    return value[0];
                }
                if (typeof value === "string" && value.length > 0) {
                    return value;
                }
            }
        }

        return t("vue.server_failed_message");
    };

    async function apply() {
        if (submitting.value) return;
        submitting.value = true;

        try {
            await nextTick();

            const response = await axios.post(
                route("dns.apply"),
                {
                    domain_name: toValue(domainName),
                    desired_dmarc_record: desiredRecord.value,
                    user_source_destination: toValue(userSourceDestination),
                },
                { headers: { Accept: "application/json" } },
            );

            const redirectUrl = response?.data?.redirect_url;
            if (typeof redirectUrl === "string" && redirectUrl.length > 0) {
                window.location.assign(redirectUrl);
                return;
            }

            if (response?.data?.success === false) {
                create({
                    title:
                        response.data.message || t("vue.server_failed_message"),
                    type: "error",
                });
                return;
            }

            create({
                title: t("vue.server_failed_message"),
                type: "error",
            });
        } catch (error) {
            create({
                title: getErrorMessage(error),
                type: "error",
            });
        } finally {
            submitting.value = false;
        }
    }

    return { apply, submitting };
}
